import { PageHeader } from '@/modules/common/components/PageHeader';
import { useCurrentUser } from '@/modules/common/hooks/useUser';
import { ApiError } from '@/services/api';
import { PaymentType } from '@/types/payment';
import { PaymentGroupType } from '@/types/payment-groups';
import { UserRole } from '@/types/user';
import { List } from '@mui/icons-material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Tab from '@mui/material/Tab';
import { getRouteApi, Link, Navigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { ListPaymentsToPayTab } from '../components/ListPaymentsToPayTab/ListPaymentsToPayTab';
import { ListPaymentsToReceive } from '../components/ListPaymentsToReceive';
import { PaymentPageTab, PaymentsSearchParams } from '../types';

const routeApi = getRouteApi('/_admin/payments/');

const ALLOWED_TABS_BY_USER_ROLES: Record<UserRole, PaymentPageTab[]> = {
  [UserRole.Employer]: [PaymentPageTab.TO_PAY],
  [UserRole.EducationProvider]: [
    PaymentPageTab.TO_RECEIVE,
    PaymentPageTab.TO_PAY,
  ],
  [UserRole.SuperAdmin]: [],
  [UserRole.User]: [],
  [UserRole.Applicant]: [],
  [UserRole.TaxBenefitsProvider]: [],
};

const TAB_INFO = {
  [PaymentPageTab.TO_RECEIVE]: {
    label: 'To receive',
    icon: <List />,
  },
  [PaymentPageTab.TO_PAY]: {
    label: 'To Pay',
    icon: <List />,
  },
};

export function ListPaymentsPage() {
  const search = routeApi.useSearch() as PaymentsSearchParams;

  const { data: { data: user } = {}, isLoading: isLoadingUser } =
    useCurrentUser();

  const allowableTabs = useMemo(
    () =>
      Object.entries(ALLOWED_TABS_BY_USER_ROLES)
        .filter(([role, tabs]) => {
          return user?.roles.find(r => r === role) && tabs.length;
        })
        .map(([_, tabs]) => tabs)
        .flat(),
    [user?.roles],
  );

  const defaultTab = allowableTabs[0];
  const isEducationProvider = user?.roles.includes(UserRole.EducationProvider);
  const paymentType = isEducationProvider
    ? (search.tab ?? defaultTab) === PaymentPageTab.TO_PAY
      ? PaymentType.OriginationCommission
      : PaymentType.RepaymentToEP
    : undefined;
  const paymentGroupType = isEducationProvider
    ? (search.tab ?? defaultTab) === PaymentPageTab.TO_PAY
      ? PaymentGroupType.PayOut
      : PaymentGroupType.PayIn
    : undefined;

  if (
    !isLoadingUser &&
    !allowableTabs.includes((search.tab ?? '') as PaymentPageTab)
  ) {
    if (defaultTab && defaultTab !== search.tab) {
      return <Navigate to={`/payments/?tab=${defaultTab}`} />;
    }

    throw new ApiError('Forbidden', 403);
  }

  return (
    <div className="flex flex-col min-h-full">
      <PageHeader pageTitle="Payments" />
      {!isLoadingUser && (
        <>
          <TabContext value={search.tab ?? defaultTab}>
            <TabList className="flex gap-5 px-8 border-b mt-8">
              {allowableTabs.map(tab => (
                <Tab
                  key={tab}
                  value={tab}
                  label={
                    <Link
                      className="flex gap-2 items-center"
                      search={{ tab }}
                      activeProps={{
                        className: 'text-primary',
                      }}
                    >
                      {TAB_INFO[tab].icon}
                      {TAB_INFO[tab].label}
                    </Link>
                  }
                />
              ))}
            </TabList>

            <TabPanel
              value={PaymentPageTab.TO_PAY}
              className="flex-1 px-8 pt-4 flex flex-col empty:hidden"
            >
              <ListPaymentsToPayTab
                paymentType={paymentType}
                paymentGroupType={paymentGroupType}
              />
            </TabPanel>

            <TabPanel
              value={PaymentPageTab.TO_RECEIVE}
              className="flex-1 px-8 pt-4 flex flex-col empty:hidden"
            >
              <ListPaymentsToReceive
                paymentGroupType={paymentGroupType}
                paymentType={paymentType}
              />
            </TabPanel>
          </TabContext>
        </>
      )}
    </div>
  );
}
